import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ComposableMap, Geographies, Geography, GeoFeature } from 'react-simple-maps';
import { TrendingUp, MapPin, Users, ChevronUp, ChevronDown, DollarSign, ShoppingBag, Receipt, ArrowLeft, Layers, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { businessDashboardService, StateSalesRow, CitySalesRow, CustomerSalesRow, FabricSalesRow, ProductType, ProductTypeSalesRow } from '../../api/services/businessDashboardService';
import { MainLayout } from '../../components/layout/MainLayout';

/* ─── DARK MODE HOOK ───────────────────────────────────────────────────── */

const useIsDark = () => {
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return isDark;
};

/* ─── CONSTANTS ────────────────────────────────────────────────────────── */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const FIPS_TO_STATE: Record<string, string> = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA',
  '08': 'CO', '09': 'CT', '10': 'DE', '11': 'DC', '12': 'FL',
  '13': 'GA', '15': 'HI', '16': 'ID', '17': 'IL', '18': 'IN',
  '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA', '23': 'ME',
  '24': 'MD', '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS',
  '29': 'MO', '30': 'MT', '31': 'NE', '32': 'NV', '33': 'NH',
  '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND',
  '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI',
  '45': 'SC', '46': 'SD', '47': 'TN', '48': 'TX', '49': 'UT',
  '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV', '55': 'WI',
  '56': 'WY',
};

/** Logarithmic interpolation through indigo stops so mid-tier states show clearly. */
const getStateColor = (revenue: number, maxRevenue: number): string => {
  if (!revenue || !maxRevenue) return '#d1d5db'; // gray-300 — no data
  const ratio = Math.log(revenue + 1) / Math.log(maxRevenue + 1);
  const stops = [
    [224, 231, 255], // indigo-100
    [165, 180, 252], // indigo-300
    [99,  102, 241], // indigo-500
    [67,  56,  202], // indigo-700
    [49,  46,  129], // indigo-900
  ];
  const scaled = ratio * (stops.length - 1);
  const lo = Math.floor(scaled);
  const hi = Math.min(lo + 1, stops.length - 1);
  const t  = scaled - lo;
  const r = Math.round(stops[lo][0] + t * (stops[hi][0] - stops[lo][0]));
  const g = Math.round(stops[lo][1] + t * (stops[hi][1] - stops[lo][1]));
  const b = Math.round(stops[lo][2] + t * (stops[hi][2] - stops[lo][2]));
  return `rgb(${r},${g},${b})`;
};

/* ─── FORMATTERS ───────────────────────────────────────────────────────── */

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const fmtShort = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
};

const fmtFull = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

/* ─── SHARED COMPONENTS ────────────────────────────────────────────────── */

interface YearSelectProps {
  value: number;
  onChange: (y: number) => void;
  label: string;
  includeAll?: boolean;
}

const YearSelect: React.FC<YearSelectProps> = ({ value, onChange, label, includeAll }) => {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{label}</span>
      <select
        value={value}
        onChange={e => onChange(parseInt(e.target.value))}
        className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        {includeAll && <option value={0}>All Years</option>}
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
};

const MonthSelect: React.FC<{ value: number; onChange: (m: number) => void; disabled?: boolean }> = ({ value, onChange, disabled }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Month:</span>
    <select
      value={value}
      disabled={disabled}
      onChange={e => onChange(parseInt(e.target.value))}
      className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <option value={0}>All Months</option>
      {MONTHS.map((name, i) => (
        <option key={i + 1} value={i + 1}>{name}</option>
      ))}
    </select>
  </div>
);

const Card: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  controls?: React.ReactNode;
}> = ({ title, icon, children, controls }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
      <div className="flex items-center gap-2 text-gray-800 dark:text-white font-semibold text-sm">
        {icon}{title}
      </div>
      {controls && <div className="flex items-center gap-3">{controls}</div>}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Skeleton: React.FC<{ h?: string }> = ({ h = 'h-64' }) => (
  <div className={`${h} rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse`} />
);

const SalesTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-700 dark:text-gray-200 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</p>
      ))}
    </div>
  );
};

/* ─── REVENUE KPI ROW ──────────────────────────────────────────────────── */

const RevenueKpiRow: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const prevYear = year - 1;

  const { data = [], isLoading } = useQuery({
    queryKey: ['business-dashboard', 'summary', year, prevYear],
    queryFn: () => businessDashboardService.getRevenueSummary(year, prevYear),
    staleTime: 5 * 60 * 1000,
  });

  const current  = data.find(r => r.Year === year);
  const previous = data.find(r => r.Year === prevYear);

  const revenueChange = previous?.TotalRevenue && previous.TotalRevenue > 0
    ? ((( current?.TotalRevenue ?? 0) - previous.TotalRevenue) / previous.TotalRevenue) * 100
    : null;

  const ordersChange = previous?.TotalOrders && previous.TotalOrders > 0
    ? (((current?.TotalOrders ?? 0) - previous.TotalOrders) / previous.TotalOrders) * 100
    : null;

  const avgChange = previous?.AvgOrderValue && previous.AvgOrderValue > 0
    ? (((current?.AvgOrderValue ?? 0) - previous.AvgOrderValue) / previous.AvgOrderValue) * 100
    : null;

  const KpiCard: React.FC<{
    label: string;
    value: string;
    sub: string;
    change: number | null;
    icon: React.ReactNode;
    accent: string;
  }> = ({ label, value, sub, change, icon, accent }) => (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 flex flex-col gap-3`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</span>
        <span className={`p-2 rounded-lg ${accent}`}>{icon}</span>
      </div>

      {isLoading ? (
        <div className="h-8 w-32 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
      ) : (
        <p className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">{value}</p>
      )}

      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400 dark:text-gray-500">{sub}</span>
        {change !== null && !isLoading && (
          <span className={`inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full
            ${change >= 0
              ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20'
              : 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20'
            }`}
          >
            {change >= 0 ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {Math.abs(change).toFixed(1)}% vs {prevYear}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Overview
          <span className="normal-case font-normal tracking-normal text-gray-400 dark:text-gray-500"> · Seat Covers + Dash Covers + Cargo Liners</span>
        </p>
        <YearSelect value={year} onChange={setYear} label="Year:" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          label="Total Revenue"
          value={isLoading ? '—' : fmt(current?.TotalRevenue ?? 0)}
          sub={`${year} full year`}
          change={revenueChange}
          icon={<DollarSign size={16} className="text-indigo-600 dark:text-indigo-400" />}
          accent="bg-indigo-50 dark:bg-indigo-900/30"
        />
        <KpiCard
          label="Total Orders"
          value={isLoading ? '—' : (current?.TotalOrders ?? 0).toLocaleString()}
          sub={`${year} full year`}
          change={ordersChange}
          icon={<ShoppingBag size={16} className="text-emerald-600 dark:text-emerald-400" />}
          accent="bg-emerald-50 dark:bg-emerald-900/30"
        />
        <KpiCard
          label="Avg Order Value"
          value={isLoading ? '—' : fmt(current?.AvgOrderValue ?? 0)}
          sub={`per order in ${year}`}
          change={avgChange}
          icon={<Receipt size={16} className="text-violet-600 dark:text-violet-400" />}
          accent="bg-violet-50 dark:bg-violet-900/30"
        />
      </div>
    </div>
  );
};

/* ─── MONTHLY SALES CHART ──────────────────────────────────────────────── */

const PRODUCT_TYPES: { type: ProductType; label: string; color: string; tile: string; text: string }[] = [
  { type: 'Seat Cover',  label: 'Seat Covers',  color: '#6366f1', tile: 'bg-indigo-50 dark:bg-indigo-900/20', text: 'text-indigo-600 dark:text-indigo-400' },
  { type: 'Dash Cover',  label: 'Dash Covers',  color: '#f59e0b', tile: 'bg-amber-50 dark:bg-amber-900/20',   text: 'text-amber-600 dark:text-amber-400' },
  { type: 'Cargo Liner', label: 'Cargo Liners', color: '#14b8a6', tile: 'bg-teal-50 dark:bg-teal-900/20',     text: 'text-teal-600 dark:text-teal-400' },
];

/** Revenue per product type, keyed by chart point ("<month>" or "<month>-<day>") then year. */
type ProductBreakdown = Record<string, Record<number, Partial<Record<ProductType, number>>>>;

const MonthlySalesTooltip: React.FC<any> = ({ active, payload, label, primaryYear, compareYear, breakdown, isFiltered }) => {
  if (!active || !payload?.length) return null;
  const p1 = payload.find((p: any) => String(p.name) === String(primaryYear))?.value ?? 0;
  const p2 = payload.find((p: any) => String(p.name) === String(compareYear))?.value ?? 0;
  const diff = p1 - p2;
  const point = payload[0]?.payload;
  const key = point ? (isFiltered ? `${point.monthIndex}-${point.dayIndex}` : String(point.monthIndex)) : '';
  const split = (breakdown as ProductBreakdown | undefined)?.[key];
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-700 dark:text-gray-200 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</p>
      ))}
      {p2 > 0 && (
        <div className={`mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-700 flex items-center gap-1 font-semibold
          ${diff >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
          {diff >= 0 ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          {fmt(Math.abs(diff))} ({Math.abs((diff / p2) * 100).toFixed(1)}%)
        </div>
      )}
      {split && (
        <table className="mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-700 w-full tabular-nums">
          <thead>
            <tr className="text-gray-400 dark:text-gray-500">
              <th className="text-left font-medium pr-3 pt-1.5">Product</th>
              <th className="text-right font-medium pr-3 pt-1.5">{primaryYear}</th>
              <th className="text-right font-medium pt-1.5">{compareYear}</th>
            </tr>
          </thead>
          <tbody>
            {PRODUCT_TYPES.map(pt => (
              <tr key={pt.type} className="text-gray-600 dark:text-gray-300">
                <td className="pr-3">
                  <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: pt.color }} />
                  {pt.label}
                </td>
                <td className="text-right pr-3">{fmt(split[primaryYear]?.[pt.type] ?? 0)}</td>
                <td className="text-right">{fmt(split[compareYear]?.[pt.type] ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const MonthlySalesChart: React.FC = () => {
  const isDark = useIsDark();
  const tickColor = isDark ? '#ffffff' : '#6b7280';

  const currentYear = new Date().getFullYear();
  const [primaryYear, setPrimaryYear] = useState(currentYear);
  const [compareYear, setCompareYear] = useState(currentYear - 1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');

  const isFiltered = startDate !== '' && endDate !== '';

  const parsedStart = useMemo(() => {
    if (!startDate) return null;
    const [y, m, d] = startDate.split('-').map(Number);
    return { year: y, month: m, day: d };
  }, [startDate]);

  const parsedEnd = useMemo(() => {
    if (!endDate) return null;
    const [y, m, d] = endDate.split('-').map(Number);
    return { year: y, month: m, day: d };
  }, [endDate]);

  const effectiveFrom = parsedStart?.month ?? 1;
  const effectiveTo   = parsedEnd?.month   ?? 12;
  const fromDay       = parsedStart?.day   ?? 1;
  const toDay         = parsedEnd?.day     ?? 31;

  useEffect(() => {
    if (parsedStart) setPrimaryYear(parsedStart.year);
  }, [parsedStart]);

  // Seat Cover / Dash Cover / Cargo Liner sales, per day (follows the date range when one is set).
  // Drives the chart lines, the totals tiles and the product-line tiles, so they all reconcile.
  const { data: productData = [], isLoading: productLoading, isError: productError } = useQuery<ProductTypeSalesRow[]>({
    queryKey: ['business-dashboard', 'sales-by-product-type', primaryYear, compareYear, effectiveFrom, effectiveTo, fromDay, toDay],
    queryFn: () => businessDashboardService.getSalesByProductType(primaryYear, compareYear, effectiveFrom, effectiveTo, fromDay, toDay),
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = productLoading;
  const isError   = productError;

  const productBreakdown = useMemo<ProductBreakdown>(() => {
    const out: ProductBreakdown = {};
    for (const r of productData) {
      const k = isFiltered ? `${r.Month}-${r.Day}` : String(r.Month);
      const byYear = (out[k] ??= {});
      const byType = (byYear[r.Year] ??= {});
      byType[r.ProductType] = (byType[r.ProductType] ?? 0) + r.Revenue;
    }
    return out;
  }, [productData, isFiltered]);

  const productTotals = useMemo(() => {
    const sum = (year: number, type: ProductType, field: 'Revenue' | 'Units') =>
      productData.filter(r => r.Year === year && r.ProductType === type).reduce((s, r) => s + r[field], 0);
    const rows = PRODUCT_TYPES.map(pt => ({
      ...pt,
      rev1:   sum(primaryYear, pt.type, 'Revenue'),
      rev2:   sum(compareYear, pt.type, 'Revenue'),
      units1: sum(primaryYear, pt.type, 'Units'),
    }));
    const all1 = rows.reduce((s, r) => s + r.rev1, 0);
    return rows.map(r => ({
      ...r,
      share: all1 > 0 ? (r.rev1 / all1) * 100 : 0,
      change: r.rev2 > 0 ? ((r.rev1 - r.rev2) / r.rev2) * 100 : null,
    }));
  }, [productData, primaryYear, compareYear]);

  // Monthly chart data (full year, 12 points)
  const pointTotal = (key: string, year: number) =>
    Object.values(productBreakdown[key]?.[year] ?? {}).reduce((s, v) => s + (v ?? 0), 0);

  const monthlyChartData = useMemo(() => {
    if (isFiltered) return [];
    return MONTHS.map((month, i) => ({
      label: month,
      monthIndex: i + 1,
      [primaryYear]: pointTotal(String(i + 1), primaryYear),
      [compareYear]: pointTotal(String(i + 1), compareYear),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productBreakdown, primaryYear, compareYear, isFiltered]);

  // Daily chart data — one point per calendar day in the exact date range
  const dailyChartData = useMemo(() => {
    if (!isFiltered) return [];
    const rows: { label: string; monthIndex: number; dayIndex: number; [key: number]: number }[] = [];
    for (let m = effectiveFrom; m <= effectiveTo; m++) {
      const daysInMonth = new Date(primaryYear, m, 0).getDate();
      const startDay = m === effectiveFrom ? fromDay : 1;
      const endDay   = m === effectiveTo   ? Math.min(toDay, daysInMonth) : daysInMonth;
      for (let d = startDay; d <= endDay; d++) {
        const k = `${m}-${d}`;
        rows.push({
          label: `${MONTHS[m - 1]} ${d}`,
          monthIndex: m,
          dayIndex: d,
          [primaryYear]: pointTotal(k, primaryYear),
          [compareYear]: pointTotal(k, compareYear),
        });
      }
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productBreakdown, effectiveFrom, effectiveTo, fromDay, toDay, primaryYear, compareYear, isFiltered]);

  // Ticks: first point + 1st of each month + ~every 7 days
  const dailyTicks = useMemo(() => {
    if (!dailyChartData.length) return [];
    const ticks: string[] = [];
    let lastTickIdx = -7;
    dailyChartData.forEach((r, i) => {
      if (i === 0 || r.dayIndex === 1 || i - lastTickIdx >= 7) {
        ticks.push(r.label);
        lastTickIdx = i;
      }
    });
    return ticks;
  }, [dailyChartData]);

  const activeChartData = isFiltered ? dailyChartData : monthlyChartData;
  const dotRadius = isFiltered ? 0 : 3;

  // Totals = Seat Covers + Dash Covers + Cargo Liners
  const totals = useMemo(() => {
    const t1 = productTotals.reduce((s, r) => s + r.rev1, 0);
    const t2 = productTotals.reduce((s, r) => s + r.rev2, 0);
    const diff = t2 > 0 ? ((t1 - t2) / t2) * 100 : 0;
    return { t1, t2, diff };
  }, [productTotals]);

  const controls = (
    <>
      <YearSelect value={compareYear} onChange={setCompareYear} label="Compare:" />
      <YearSelect value={primaryYear} onChange={setPrimaryYear} label="Year:" />
    </>
  );

  return (
    <Card title="Monthly Sales — Year Over Year" icon={<TrendingUp size={16} className="text-indigo-500" />} controls={controls}>
      {/* Date range filter */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-5 pb-4 border-b border-gray-100 dark:border-gray-700">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Date Range:</span>
        <input
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <span className="text-xs text-gray-400 dark:text-gray-500">to</span>
        <input
          type="date"
          value={endDate}
          min={startDate || undefined}
          onChange={e => setEndDate(e.target.value)}
          className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        {isFiltered && (
          <button
            onClick={() => { setStartDate(''); setEndDate(''); }}
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Reset
          </button>
        )}
        {!isFiltered && (
          <span className="text-xs text-gray-400 dark:text-gray-500">Showing full year · pick dates for daily view</span>
        )}
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3">
          <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
            {primaryYear}{isFiltered ? ` · ${startDate} – ${endDate}` : ''} Total
          </p>
          <p className="text-base font-bold text-indigo-700 dark:text-indigo-300 mt-0.5">{isLoading ? '—' : fmtFull(totals.t1)}</p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
            {compareYear}{isFiltered ? ` · ${startDate.replace(String(primaryYear), String(compareYear))} – ${endDate.replace(String(primaryYear), String(compareYear))}` : ''} Total
          </p>
          <p className="text-base font-bold text-gray-700 dark:text-gray-200 mt-0.5">{isLoading ? '—' : fmtFull(totals.t2)}</p>
        </div>
        <div className={`rounded-lg p-3 ${totals.diff >= 0 ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
          <p className={`text-xs font-medium ${totals.diff >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
            {isFiltered ? 'Period' : 'YoY'} Change
          </p>
          <div className="flex items-center gap-1 mt-0.5">
            {totals.diff >= 0
              ? <ChevronUp size={18} className="text-emerald-600 dark:text-emerald-400" />
              : <ChevronDown size={18} className="text-red-600 dark:text-red-400" />}
            <p className={`text-lg font-bold ${totals.diff >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
              {Math.abs(totals.diff).toFixed(1)}%
            </p>
          </div>
        </div>
      </div>

      {/* Product line attribution */}
      <div className="mb-5">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
          Sales by Product Line · {primaryYear}{isFiltered ? ` · ${startDate} – ${endDate}` : ''}
          <span className="text-gray-400 dark:text-gray-500"> (gross item sales, before discounts, freight &amp; tax)</span>
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {productTotals.map(pt => (
            <div key={pt.type} className={`${pt.tile} rounded-lg p-3`}>
              <div className="flex items-center justify-between">
                <p className={`text-xs font-medium ${pt.text}`}>{pt.label}</p>
                {!productLoading && !productError && (
                  <span className={`text-xs font-semibold ${pt.text}`}>{pt.share.toFixed(1)}%</span>
                )}
              </div>
              {productLoading ? (
                <div className="h-6 w-28 mt-1 bg-white/60 dark:bg-gray-700 rounded animate-pulse" />
              ) : productError ? (
                <p className="text-xs text-red-500 mt-1">Failed to load</p>
              ) : (
                <>
                  <p className="text-base font-bold text-gray-800 dark:text-gray-100 mt-0.5 tabular-nums">{fmtFull(pt.rev1)}</p>
                  <div className="mt-1.5 h-1.5 rounded-full bg-white/70 dark:bg-gray-700 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pt.share}%`, backgroundColor: pt.color }} />
                  </div>
                  <div className="flex items-center justify-between mt-1.5 text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                    <span>{pt.units1.toLocaleString()} units</span>
                    {pt.change !== null ? (
                      <span className={`inline-flex items-center gap-0.5 font-semibold ${pt.change >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                        {pt.change >= 0 ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        {Math.abs(pt.change).toFixed(1)}% vs {compareYear}
                      </span>
                    ) : (
                      <span>{compareYear}: {fmt(pt.rev2)}</span>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {isLoading ? <Skeleton h="h-72" /> : isError ? (
        <div className="h-72 flex items-center justify-center text-red-500 text-sm">Failed to load data</div>
      ) : (
        <ResponsiveContainer width="100%" height={288}>
          <LineChart data={activeChartData} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
            {isFiltered ? (
              <XAxis
                dataKey="label"
                ticks={dailyTicks}
                tick={{ fontSize: 11, fill: tickColor }}
                interval={0}
              />
            ) : (
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: tickColor }} />
            )}
            <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11, fill: tickColor }} width={64} />
            <Tooltip content={<MonthlySalesTooltip primaryYear={primaryYear} compareYear={compareYear} breakdown={productBreakdown} isFiltered={isFiltered} />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey={primaryYear} stroke="#6366f1" strokeWidth={2.5} dot={dotRadius > 0 ? { r: dotRadius } : false} activeDot={{ r: 5 }} />
            <Line type="monotone" dataKey={compareYear} stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 3" dot={dotRadius > 0 ? { r: dotRadius } : false} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};

/* ─── MAP TOOLTIP ──────────────────────────────────────────────────────── */

interface MapTooltip {
  x: number;
  y: number;
  abbr: string;
  year1Revenue: number;
  year2Revenue: number;
  year1Orders: number;
}

/* ─── CITY DRILL-DOWN SIDEBAR ──────────────────────────────────────────── */

const CityDrillDown: React.FC<{
  state: string;
  stateRevenue: number;
  year: number;
  month: number;
  onBack: () => void;
}> = ({ state, stateRevenue, year, month, onBack }) => {
  const { data: cities = [], isLoading } = useQuery<CitySalesRow[]>({
    queryKey: ['business-dashboard', 'sales-by-city', state, year, month],
    queryFn: () => businessDashboardService.getSalesByCity(state, year, month),
    staleTime: 5 * 60 * 1000,
  });

  const maxCityRevenue = useMemo(() => Math.max(...cities.map(c => c.Revenue), 1), [cities]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={onBack}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
          title="Back to states"
        >
          <ArrowLeft size={14} />
        </button>
        <div>
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">
            {state} — Cities
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500">{fmt(stateRevenue)} total</p>
        </div>
      </div>

      {/* City list */}
      <div className="flex-1 overflow-y-auto space-y-2 pr-1" style={{ maxHeight: 320 }}>
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-4 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
          ))
        ) : cities.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-500">No city data available.</p>
        ) : (
          cities.map((city, i) => (
            <div key={city.City} className="flex items-center gap-2">
              <span className="w-4 text-xs text-gray-400 text-right flex-shrink-0">{i + 1}</span>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate flex-1" title={city.City}>
                {city.City}
              </span>
              <div className="w-12 bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden flex-shrink-0">
                <div
                  className="h-1.5 rounded-full bg-emerald-500"
                  style={{ width: `${(city.Revenue / maxCityRevenue) * 100}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 w-12 text-right tabular-nums flex-shrink-0">
                {fmtShort(city.Revenue)}
              </span>
            </div>
          ))
        )}
      </div>

      {!isLoading && cities.length > 0 && (
        <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400 dark:text-gray-500">
          {cities.length} cities · {cities.reduce((s, c) => s + c.OrderCount, 0).toLocaleString()} orders
        </div>
      )}
    </div>
  );
};

/* ─── SALES BY STATE MAP ───────────────────────────────────────────────── */

const SalesByStateChart: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [year1, setYear1] = useState(currentYear);
  const [year2, setYear2] = useState(currentYear - 1);
  const [month, setMonth] = useState(0);
  const [tooltip, setTooltip] = useState<MapTooltip | null>(null);
  const [selectedState, setSelectedState] = useState<string | null>(null);

  const { data = [], isLoading, isError } = useQuery({
    queryKey: ['business-dashboard', 'sales-by-state', year1, year2, month],
    queryFn: () => businessDashboardService.getSalesByState(year1, year2, month),
    staleTime: 5 * 60 * 1000,
  });

  const byState = useMemo(() => {
    const map: Record<string, StateSalesRow> = {};
    for (const row of data) {
      if (row.State) map[row.State.trim().toUpperCase()] = row;
    }
    return map;
  }, [data]);

  const maxRevenue  = useMemo(() => Math.max(...data.map(r => r.Year1Revenue), 1), [data]);
  const top10       = useMemo(() => data.slice(0, 10), [data]);
  const grandTotal1 = useMemo(() => data.reduce((s, r) => s + r.Year1Revenue, 0), [data]);
  const grandTotal2 = useMemo(() => data.reduce((s, r) => s + r.Year2Revenue, 0), [data]);

  const selectedRow = selectedState ? byState[selectedState] : null;

  const periodLabel = month === 0 ? '' : ` · ${MONTHS[month - 1]}`;

  const resetFilters = (v: number, setter: (n: number) => void) => {
    setter(v);
    setSelectedState(null);
  };

  const controls = (
    <>
      {selectedState && (
        <button
          onClick={() => setSelectedState(null)}
          className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
        >
          <ArrowLeft size={12} /> All States
        </button>
      )}
      <MonthSelect value={month} onChange={v => { setMonth(v); setSelectedState(null); }} />
      <YearSelect value={year2} onChange={v => resetFilters(v, setYear2)} label="Compare:" />
      <YearSelect value={year1} onChange={v => resetFilters(v, setYear1)} label="Year:" />
    </>
  );

  return (
    <Card title="Total Sales by State" icon={<MapPin size={16} className="text-emerald-500" />} controls={controls}>
      {isLoading ? <Skeleton h="h-96" /> : isError ? (
        <div className="h-96 flex items-center justify-center text-red-500 text-sm">Failed to load data</div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── Choropleth map (colored by year1) ── */}
          <div className="flex-1 min-w-0">
            <ComposableMap projection="geoAlbersUsa" style={{ width: '100%', height: 'auto' }}>
              <Geographies geography={GEO_URL}>
                {({ geographies }: { geographies: GeoFeature[] }) =>
                  geographies.map((geo: GeoFeature) => {
                    const fips       = String(geo.id).padStart(2, '0');
                    const abbr       = FIPS_TO_STATE[fips] ?? '';
                    const row        = byState[abbr];
                    const rev1       = row?.Year1Revenue ?? 0;
                    const isSelected = abbr === selectedState;

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={isSelected ? '#f59e0b' : getStateColor(rev1, maxRevenue)}
                        stroke="#ffffff"
                        strokeWidth={isSelected ? 1.5 : 0.6}
                        onMouseEnter={(e: React.MouseEvent<SVGPathElement>) =>
                          setTooltip({
                            x: e.clientX, y: e.clientY,
                            abbr: abbr || '??',
                            year1Revenue: rev1,
                            year2Revenue: row?.Year2Revenue ?? 0,
                            year1Orders:  row?.Year1Orders  ?? 0,
                          })
                        }
                        onMouseMove={(e: React.MouseEvent<SVGPathElement>) =>
                          setTooltip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null)
                        }
                        onMouseLeave={() => setTooltip(null)}
                        onClick={() => { if (abbr) { setSelectedState(abbr); setTooltip(null); } }}
                        style={{
                          default: { outline: 'none' },
                          hover:   { fill: '#fbbf24', outline: 'none', cursor: 'pointer' },
                          pressed: { fill: '#d97706', outline: 'none' },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>

            {/* Gradient legend */}
            <div className="flex items-center justify-center gap-3 mt-1">
              <span className="text-xs text-gray-400 dark:text-gray-500">$0</span>
              <div className="h-2 w-40 rounded-full"
                style={{ background: 'linear-gradient(to right, rgb(224,231,255), rgb(99,102,241), rgb(49,46,129))' }}
              />
              <span className="text-xs text-gray-400 dark:text-gray-500">{fmtShort(maxRevenue)}</span>
              <span className="ml-2 inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                <span className="inline-block w-3 h-3 rounded-sm bg-gray-300" /> No data
              </span>
            </div>
          </div>

          {/* ── Sidebar ── */}
          <div className="lg:w-60 flex-shrink-0">
            {selectedState && selectedRow ? (
              <CityDrillDown
                state={selectedState}
                stateRevenue={selectedRow.Year1Revenue}
                year={year1}
                month={month}
                onBack={() => setSelectedState(null)}
              />
            ) : (
              <>
                {/* Column headers */}
                <div className="flex items-center gap-1 mb-2 text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                  <span className="w-4" />
                  <span className="w-7" />
                  <span className="flex-1 text-right">{year1}{periodLabel}</span>
                  <span className="w-16 text-right">YoY</span>
                </div>

                <div className="space-y-1.5">
                  {top10.map((row, i) => {
                    const yoy = row.Year2Revenue > 0
                      ? ((row.Year1Revenue - row.Year2Revenue) / row.Year2Revenue) * 100
                      : null;
                    return (
                      <button
                        key={row.State}
                        onClick={() => setSelectedState(row.State)}
                        className="flex items-center gap-1 w-full text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded px-1 py-0.5 transition-colors group"
                      >
                        <span className="w-4 text-xs text-gray-400 text-right flex-shrink-0">{i + 1}</span>
                        <span className="w-7 text-xs font-semibold text-gray-700 dark:text-gray-300 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 flex-shrink-0">
                          {row.State}
                        </span>
                        <span className="flex-1 text-right text-xs font-medium text-gray-800 dark:text-white tabular-nums">
                          {fmtShort(row.Year1Revenue)}
                        </span>
                        <span className="w-16 text-right flex-shrink-0">
                          {yoy !== null ? (
                            <span className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full
                              ${yoy >= 0
                                ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20'
                                : 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20'
                              }`}
                            >
                              {yoy >= 0 ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                              {Math.abs(yoy).toFixed(1)}%
                            </span>
                          ) : (
                            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">New</span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {grandTotal1 > 0 && (
                  <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-0.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400 dark:text-gray-500">{year1}{periodLabel}</span>
                      <span className="font-semibold text-gray-700 dark:text-gray-200 tabular-nums">{fmt(grandTotal1)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400 dark:text-gray-500">{year2}{periodLabel}</span>
                      <span className="text-gray-500 dark:text-gray-400 tabular-nums">{fmt(grandTotal2)}</span>
                    </div>
                    {grandTotal2 > 0 && (() => {
                      const d = ((grandTotal1 - grandTotal2) / grandTotal2) * 100;
                      return (
                        <div className="flex justify-between items-center text-xs pt-0.5">
                          <span className="text-gray-400 dark:text-gray-500">YoY</span>
                          <span className={`inline-flex items-center gap-0.5 font-semibold
                            ${d >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}
                          >
                            {d >= 0 ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                            {Math.abs(d).toFixed(1)}%
                          </span>
                        </div>
                      );
                    })()}
                    <p className="text-xs text-gray-400 dark:text-gray-500 pt-0.5">{data.length} states · click to drill down</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Fixed map tooltip */}
      {tooltip && (() => {
        const yoy = tooltip.year2Revenue > 0
          ? ((tooltip.year1Revenue - tooltip.year2Revenue) / tooltip.year2Revenue) * 100
          : null;
        return (
          <div
            className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 pointer-events-none shadow-xl"
            style={{ left: tooltip.x + 14, top: tooltip.y - 64 }}
          >
            <p className="font-bold text-sm mb-1">{tooltip.abbr}</p>
            <div className="space-y-0.5">
              <div className="flex items-center justify-between gap-4">
                <span className="text-gray-400">{year1}{periodLabel}</span>
                <span className="font-semibold text-white tabular-nums">{fmt(tooltip.year1Revenue)}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-gray-400">{year2}{periodLabel}</span>
                <span className="text-gray-300 tabular-nums">{fmt(tooltip.year2Revenue)}</span>
              </div>
              {yoy !== null && (
                <div className="flex items-center justify-between gap-4 pt-0.5 border-t border-gray-700">
                  <span className="text-gray-400">YoY</span>
                  <span className={`font-semibold inline-flex items-center gap-0.5 ${yoy >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {yoy >= 0 ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                    {Math.abs(yoy).toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
            <p className="text-gray-500 mt-1.5">{tooltip.year1Orders.toLocaleString()} orders · click to drill down</p>
          </div>
        );
      })()}
    </Card>
  );
};

/* ─── SALES BY CUSTOMER TABLE ──────────────────────────────────────────── */

const SalesByCustomerTable: React.FC = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [year1, setYear1] = useState(currentYear);
  const [year2, setYear2] = useState(currentYear - 1);
  const [month, setMonth] = useState(0);

  const { data = [], isLoading, isError } = useQuery<CustomerSalesRow[]>({
    queryKey: ['business-dashboard', 'sales-by-customer', year1, year2, month],
    queryFn: () => businessDashboardService.getSalesByCustomer(year1, year2, month, 25),
    staleTime: 5 * 60 * 1000,
  });

  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? data.filter(r => r.CustomerName.toLowerCase().includes(q)) : data;
  }, [data, search]);

  const maxRevenue = useMemo(() => Math.max(...data.map(r => r.Year1Revenue), 1), [data]);

  const totals = useMemo(() => ({
    year1Revenue: filtered.reduce((s, r) => s + r.Year1Revenue, 0),
    year2Revenue: filtered.reduce((s, r) => s + r.Year2Revenue, 0),
    year1Orders:  filtered.reduce((s, r) => s + r.Year1Orders,  0),
  }), [filtered]);

  const periodLabel = (y: number) =>
    month === 0 ? String(y) : `${MONTHS[month - 1]} ${y}`;

  const controls = (
    <>
      <MonthSelect value={month} onChange={setMonth} />
      <YearSelect value={year2} onChange={setYear2} label="Compare:" />
      <YearSelect value={year1} onChange={setYear1} label="Year:" />
    </>
  );

  return (
    <Card title="Sales by Customer" icon={<Users size={16} className="text-violet-500" />} controls={controls}>
      {isLoading ? <Skeleton h="h-96" /> : isError ? (
        <div className="h-48 flex items-center justify-center text-red-500 text-sm">Failed to load data</div>
      ) : data.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No data available</div>
      ) : (
        <>
          {/* Search bar */}
          <div className="mb-3">
            <input
              type="text"
              placeholder="Search customer…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full sm:w-72 text-sm px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="pb-2 pr-3 font-medium w-7">#</th>
                <th className="pb-2 pr-4 font-medium">Customer</th>
                <th className="pb-2 pr-3 font-medium text-right">{periodLabel(year1)}</th>
                <th className="pb-2 pr-3 font-medium text-right">{periodLabel(year2)}</th>
                <th className="pb-2 pr-3 font-medium text-right">YoY</th>
                <th className="pb-2 font-medium text-right">Orders</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                    No customers match "{search}"
                  </td>
                </tr>
              )}
              {filtered.map((row, i) => {
                const yoy = row.Year2Revenue > 0
                  ? ((row.Year1Revenue - row.Year2Revenue) / row.Year2Revenue) * 100
                  : null;
                const isNew = row.Year2Revenue === 0 && row.Year1Revenue > 0;

                return (
                  <tr key={row.CustomerName} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="py-2.5 pr-3 text-gray-400 dark:text-gray-500 text-xs tabular-nums">{i + 1}</td>

                    <td className="py-2.5 pr-4">
                      <div className="flex flex-col gap-0.5">
                        {row.CustomerId ? (
                          <button
                            onClick={() => navigate(`/admin/customer-report/${encodeURIComponent(row.CustomerId)}`, { state: { customerName: row.CustomerName } })}
                            className="font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 text-xs leading-tight text-left hover:underline"
                          >
                            {row.CustomerName}
                          </button>
                        ) : (
                          <span className="font-medium text-gray-800 dark:text-white text-xs leading-tight">
                            {row.CustomerName}
                          </span>
                        )}
                        {/* mini bar for current year */}
                        <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1 overflow-hidden">
                          <div
                            className="h-1 rounded-full bg-indigo-500"
                            style={{ width: `${(row.Year1Revenue / maxRevenue) * 100}%` }}
                          />
                        </div>
                      </div>
                    </td>

                    <td className="py-2.5 pr-3 text-right font-semibold text-gray-800 dark:text-white tabular-nums text-xs">
                      {fmt(row.Year1Revenue)}
                    </td>

                    <td className="py-2.5 pr-3 text-right text-gray-500 dark:text-gray-400 tabular-nums text-xs">
                      {row.Year2Revenue > 0 ? fmt(row.Year2Revenue) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                    </td>

                    <td className="py-2.5 pr-3 text-right">
                      {isNew ? (
                        <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded-full">
                          New
                        </span>
                      ) : yoy !== null ? (
                        <span className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full
                          ${yoy >= 0
                            ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20'
                            : 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20'
                          }`}
                        >
                          {yoy >= 0 ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                          {Math.abs(yoy).toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                      )}
                    </td>

                    <td className="py-2.5 text-right text-gray-600 dark:text-gray-400 tabular-nums text-xs">
                      {row.Year1Orders.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 dark:border-gray-600">
                <td colSpan={2} className="pt-3 text-xs text-gray-500 dark:text-gray-400 font-medium">
                  {search ? `${filtered.length} of ${data.length}` : `Top ${data.length}`} customers
                </td>
                <td className="pt-3 text-right text-xs font-bold text-gray-800 dark:text-white tabular-nums">
                  {fmt(totals.year1Revenue)}
                </td>
                <td className="pt-3 text-right text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                  {fmt(totals.year2Revenue)}
                </td>
                <td className="pt-3 text-right">
                  {totals.year2Revenue > 0 && (() => {
                    const d = ((totals.year1Revenue - totals.year2Revenue) / totals.year2Revenue) * 100;
                    return (
                      <span className={`inline-flex items-center gap-0.5 text-xs font-semibold
                        ${d >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}
                      >
                        {d >= 0 ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                        {Math.abs(d).toFixed(1)}%
                      </span>
                    );
                  })()}
                </td>
                <td className="pt-3 text-right text-xs text-gray-600 dark:text-gray-400 tabular-nums">
                  {totals.year1Orders.toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        </>
      )}
    </Card>
  );
};

/* ─── SALES BY FABRIC TABLE ────────────────────────────────────────────── */

const SalesByFabricTable: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [year1, setYear1] = useState(currentYear);
  const [year2, setYear2] = useState(currentYear - 1);
  const [month, setMonth] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');

  const isFiltered = startDate !== '' && endDate !== '';

  const parsedStart = useMemo(() => {
    if (!startDate) return null;
    const [y, m, d] = startDate.split('-').map(Number);
    return { year: y, month: m, day: d };
  }, [startDate]);

  const parsedEnd = useMemo(() => {
    if (!endDate) return null;
    const [y, m, d] = endDate.split('-').map(Number);
    return { year: y, month: m, day: d };
  }, [endDate]);

  const effectiveFrom = parsedStart?.month ?? undefined;
  const effectiveTo   = parsedEnd?.month   ?? undefined;
  const fromDay       = parsedStart?.day   ?? undefined;
  const toDay         = parsedEnd?.day     ?? undefined;

  useEffect(() => {
    if (parsedStart) setYear1(parsedStart.year);
  }, [parsedStart]);

  const { data = [], isLoading, isError } = useQuery<FabricSalesRow[]>({
    queryKey: ['business-dashboard', 'sales-by-fabric', year1, year2, month, effectiveFrom, effectiveTo, fromDay, toDay],
    queryFn: () => businessDashboardService.getSalesByFabric(
      year1, year2, isFiltered ? 0 : month, 25,
      isFiltered ? effectiveFrom : undefined,
      isFiltered ? effectiveTo   : undefined,
      isFiltered ? fromDay       : undefined,
      isFiltered ? toDay         : undefined,
    ),
    staleTime: 5 * 60 * 1000,
  });

  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? data.filter(r => r.Material.toLowerCase().includes(q)) : data;
  }, [data, search]);

  const maxRevenue = useMemo(() => Math.max(...data.map(r => r.Year1Revenue), 1), [data]);

  const totals = useMemo(() => ({
    year1Revenue: filtered.reduce((s, r) => s + r.Year1Revenue, 0),
    year2Revenue: filtered.reduce((s, r) => s + r.Year2Revenue, 0),
    year1Orders:  filtered.reduce((s, r) => s + r.Year1Orders,  0),
  }), [filtered]);

  const periodLabel = (y: number) => {
    if (isFiltered) {
      const start = startDate.replace(String(year1), String(y));
      const end   = endDate.replace(String(year1), String(y));
      return `${start} – ${end}`;
    }
    return month === 0 ? String(y) : `${MONTHS[month - 1]} ${y}`;
  };

  const downloadCsv = () => {
    const headers = ['#', 'Fabric', periodLabel(year1), periodLabel(year2), 'YoY %', 'Orders'];
    const rows = filtered.map((row, i) => {
      const yoy = row.Year2Revenue > 0
        ? (((row.Year1Revenue - row.Year2Revenue) / row.Year2Revenue) * 100).toFixed(1)
        : '';
      return [i + 1, row.Material, row.Year1Revenue.toFixed(2), row.Year2Revenue.toFixed(2), yoy, row.Year1Orders];
    });
    const escape = (v: unknown) => {
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const suffix = isFiltered ? `${startDate}_to_${endDate}` : (month === 0 ? String(year1) : `${MONTHS[month - 1]}-${year1}`);
    a.download = `sales-by-fabric_${suffix}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const controls = (
    <>
      <MonthSelect value={month} onChange={setMonth} disabled={isFiltered} />
      <YearSelect value={year2} onChange={setYear2} label="Compare:" />
      <YearSelect value={year1} onChange={setYear1} label="Year:" />
    </>
  );

  return (
    <Card title="Sales by Fabric" icon={<Layers size={16} className="text-amber-500" />} controls={controls}>
      {/* Date range filter */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4 pb-4 border-b border-gray-100 dark:border-gray-700">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Date Range:</span>
        <input
          type="date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <span className="text-xs text-gray-400 dark:text-gray-500">to</span>
        <input
          type="date"
          value={endDate}
          min={startDate || undefined}
          onChange={e => setEndDate(e.target.value)}
          className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        {isFiltered ? (
          <button
            onClick={() => { setStartDate(''); setEndDate(''); }}
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Reset
          </button>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-500">pick dates for an exact range · otherwise uses Month/Year above</span>
        )}
      </div>

      {isLoading ? <Skeleton h="h-96" /> : isError ? (
        <div className="h-48 flex items-center justify-center text-red-500 text-sm">Failed to load data</div>
      ) : data.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No data available</div>
      ) : (
        <>
          {/* Search bar */}
          <div className="mb-3 flex items-center justify-between gap-3">
            <input
              type="text"
              placeholder="Search fabric…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full sm:w-72 text-sm px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={downloadCsv}
              disabled={filtered.length === 0}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              <Download size={13} />
              Export CSV
            </button>
          </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="pb-2 pr-3 font-medium w-7">#</th>
                <th className="pb-2 pr-4 font-medium">Fabric</th>
                <th className="pb-2 pr-3 font-medium text-right">{periodLabel(year1)}</th>
                <th className="pb-2 pr-3 font-medium text-right">{periodLabel(year2)}</th>
                <th className="pb-2 pr-3 font-medium text-right">YoY</th>
                <th className="pb-2 font-medium text-right">Orders</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                    No fabrics match "{search}"
                  </td>
                </tr>
              )}
              {filtered.map((row, i) => {
                const yoy = row.Year2Revenue > 0
                  ? ((row.Year1Revenue - row.Year2Revenue) / row.Year2Revenue) * 100
                  : null;
                const isNew = row.Year2Revenue === 0 && row.Year1Revenue > 0;

                return (
                  <tr key={row.Material} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="py-2.5 pr-3 text-gray-400 dark:text-gray-500 text-xs tabular-nums">{i + 1}</td>

                    <td className="py-2.5 pr-4">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium text-gray-800 dark:text-white text-xs leading-tight">
                          {row.Material}
                        </span>
                        {/* mini bar for current year */}
                        <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1 overflow-hidden">
                          <div
                            className="h-1 rounded-full bg-amber-500"
                            style={{ width: `${(row.Year1Revenue / maxRevenue) * 100}%` }}
                          />
                        </div>
                      </div>
                    </td>

                    <td className="py-2.5 pr-3 text-right font-semibold text-gray-800 dark:text-white tabular-nums text-xs">
                      {fmt(row.Year1Revenue)}
                    </td>

                    <td className="py-2.5 pr-3 text-right text-gray-500 dark:text-gray-400 tabular-nums text-xs">
                      {row.Year2Revenue > 0 ? fmt(row.Year2Revenue) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                    </td>

                    <td className="py-2.5 pr-3 text-right">
                      {isNew ? (
                        <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded-full">
                          New
                        </span>
                      ) : yoy !== null ? (
                        <span className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full
                          ${yoy >= 0
                            ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20'
                            : 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20'
                          }`}
                        >
                          {yoy >= 0 ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                          {Math.abs(yoy).toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                      )}
                    </td>

                    <td className="py-2.5 text-right text-gray-600 dark:text-gray-400 tabular-nums text-xs">
                      {row.Year1Orders.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 dark:border-gray-600">
                <td colSpan={2} className="pt-3 text-xs text-gray-500 dark:text-gray-400 font-medium">
                  {search ? `${filtered.length} of ${data.length}` : `Top ${data.length}`} fabrics
                </td>
                <td className="pt-3 text-right text-xs font-bold text-gray-800 dark:text-white tabular-nums">
                  {fmt(totals.year1Revenue)}
                </td>
                <td className="pt-3 text-right text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                  {fmt(totals.year2Revenue)}
                </td>
                <td className="pt-3 text-right">
                  {totals.year2Revenue > 0 && (() => {
                    const d = ((totals.year1Revenue - totals.year2Revenue) / totals.year2Revenue) * 100;
                    return (
                      <span className={`inline-flex items-center gap-0.5 text-xs font-semibold
                        ${d >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}
                      >
                        {d >= 0 ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                        {Math.abs(d).toFixed(1)}%
                      </span>
                    );
                  })()}
                </td>
                <td className="pt-3 text-right text-xs text-gray-600 dark:text-gray-400 tabular-nums">
                  {totals.year1Orders.toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        </>
      )}
    </Card>
  );
};

/* ─── PAGE ─────────────────────────────────────────────────────────────── */

const BusinessDashboard: React.FC = () => (
  <MainLayout>
    <div className="space-y-6 max-w-screen-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Business Dashboard</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Sales performance and product analytics</p>
      </div>
      <RevenueKpiRow />
      <MonthlySalesChart />
      <SalesByStateChart />
      <SalesByCustomerTable />
      <SalesByFabricTable />
    </div>
  </MainLayout>
);

export default BusinessDashboard;
